import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, MemberType } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ExpandEvent } from 'app/shared/elements/ess-table';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberEligibility extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable.string({ defaultBindingMode: bindingMode.toView })
    public memberType: MemberType = MemberType.Member;

    protected member: CD.Member | null = null;

    protected memberEligibilities: ReadonlyArray<CD.MemberEligibility> = [];

    protected eligibilityDetails: Record<string, boolean> = {};

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'member.plan.healthPlan.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        await this.loadMemberEligibilities();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected eligibilityDetailLoaded(event: ExpandEvent<CD.MemberEligibility>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.eligibilityDetails[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////


    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = this.memberType === MemberType.Member ? await this.customFieldContext.getCustomFieldTargetColumns('CD.MemberPlan') : {};
    }

    @apiCall
    private async loadMemberEligibilities(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        const includes = [
            'member',
            'member.plan',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'product'
        ];

        let fields = [
            'dateRange.end',
            'dateRange.start',
            'employer',
            'groupNumber',
            'member.plan.eligibility.rank',
            'member.plan.healthPlan.description',
            'member.plan.identifier.primary',
            'member.plan.lineOfBusiness.description',
            'product.description',
            'receivedDate',
            'shareOfCostAmount'
        ];

        if(this.memberType === MemberType.Member) {
            fields = fields.concat([
                'member.plan.custom.lookup.*',
                'member.plan.custom.text.*',
            ]);
        }

        try {
            this.state.beginLoad();
            const response = this.memberType === MemberType.Patient ?
                await this.memberService.loadPatientEligibilities(this.memberId, options, includes, fields) :
                await this.memberService.loadMemberEligibilities(this.memberId, options, includes, fields);
            this.memberEligibilities = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
